"use client";
import React, { useState, useEffect, useRef, SyntheticEvent } from "react";
import {
  Flex,
  Image as ChakraImage,
  Button,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { Stage, Layer, Line } from "react-konva";
import heic2any from "heic2any";
import { useFalForLoraImgToImgMask } from "queries"; // Import your hook here
import Konva from "konva";

interface LineState {
  tool: string;
  strokeWidth: number;
  points: number[];
}

interface StageDimensions {
  width: number;
  height: number;
}


interface ImageMaskingToolProps {
  onMaskGenerated: (maskB64: string) => void; // Callback function to return the mask
}

const ImageMaskingTool: React.FC<ImageMaskingToolProps> = ({ onMaskGenerated }) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [resizedImageFile, setResizedImageFile] = useState<File | null>(null); // Store resized image file
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [stageDimensions, setStageDimensions] = useState<StageDimensions>({
    width: 0,
    height: 0,
  });
  const stageRef = useRef<Konva.Stage | null>(null);
  const [lines, setLines] = useState<LineState[]>([]);
  const [brushSize, setBrushSize] = useState<number>(5);
  const isDrawing = useRef<boolean>(false);
  const toast = useToast();

  const { loading, error, generateImage } = useFalForLoraImgToImgMask(); // Use the hook

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 1) {
        setImageFile(acceptedFiles[0]);
      } else {
        toast({
          title: "Error",
          description: "Please upload a single image.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    },
    accept: {
      "image/png": [],
      "image/jpeg": [],
      "image/webp": [],
      "image/heic": [],
    },
    maxSize: 3 * 1024 * 1024,
    multiple: false,
  });

  useEffect(() => {
    if (imageFile) {
      const processImageFile = async () => {
        let objectURL = URL.createObjectURL(imageFile);

        if (imageFile.type === "" || imageFile.name.toLowerCase().includes(".heic")) {
          await fetch(objectURL)
            .then((res) => res.blob())
            .then((blob) =>
              heic2any({
                blob,
              })
            )
            .then((conversionResult: Blob | Blob[]) => {
              if (conversionResult instanceof Blob) {
                URL.revokeObjectURL(objectURL);
                objectURL = URL.createObjectURL(conversionResult);
              } else if (conversionResult.length > 0) {
                URL.revokeObjectURL(objectURL);
                objectURL = URL.createObjectURL(conversionResult[0]);
              }
            })
            .catch(() => {
              toast({
                title: "Error",
                description: "Failed to convert HEIC image.",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            });
        }

        setImagePreview(objectURL);
      };

      processImageFile();
    }
  }, [imageFile, toast]);

  const handleImageLoad = async (event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget as HTMLImageElement;

    console.log("Original Uploaded Image Dimensions:", {
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
    });

    const adjustedWidth = Math.floor(img.naturalWidth / 8) * 8;
    const adjustedHeight = Math.floor(img.naturalHeight / 8) * 8;

    console.log("Adjusted Image Dimensions (Divisible by 8):", {
      adjustedWidth,
      adjustedHeight,
    });

    setStageDimensions({
      width: adjustedWidth,
      height: adjustedHeight,
    });

    const canvas = document.createElement("canvas");
    canvas.width = adjustedWidth;
    canvas.height = adjustedHeight;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      const tempImage = new Image();
      tempImage.src = img.src;
      tempImage.onload = async () => {
        ctx.drawImage(tempImage, 0, 0, adjustedWidth, adjustedHeight);

        // Convert resized image to a Blob and create a new File
        canvas.toBlob((blob) => {
          if (blob) {
            const resizedFile = new File([blob], "resized_image.png", {
              type: "image/png",
            });
            setResizedImageFile(resizedFile);
            console.log("Resized Image File:", resizedFile);
          }
        });

        const resizedPreview = canvas.toDataURL();
        console.log("Resized Image Preview Base64:", resizedPreview);
        setImagePreview(resizedPreview);
      };
    }
  };

  const handleCursorDown = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    isDrawing.current = true;
    const stage = e.target.getStage();
    if (!stage) return;
    const point = stage.getPointerPosition();
    if (!point) return;
    setLines([...lines, { tool: "brush", strokeWidth: brushSize, points: [point.x, point.y] }]);
  };

  const handleCursorMove = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (!isDrawing.current) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const point = stage.getPointerPosition();
    if (!point) return;
    const lastLine = lines[lines.length - 1];
    lastLine.points = lastLine.points.concat([point.x, point.y]);
    lines.splice(lines.length - 1, 1, lastLine);
    setLines([...lines]);

     // Real-time mask logging
    if (stageRef.current) {
    const maskDataURL = stageRef.current.toDataURL();
    console.log("Real-time Mask Base64:", maskDataURL);
  }
  };

  const handleCursorUp = () => {
    isDrawing.current = false;
  };

  const handleConfirm = async () => {
    if (!resizedImageFile || !stageRef.current) {
      toast({
        title: "Error",
        description: "Please upload an image and draw a mask.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    console.log("Stage Dimensions:", stageDimensions);

    const maskCanvas = document.createElement("canvas");
    maskCanvas.width = stageDimensions.width;
    maskCanvas.height = stageDimensions.height;
    const ctx = maskCanvas.getContext("2d");

    if (ctx) {
      const dataURL = stageRef.current.toDataURL();
      const maskImage = new Image();
      maskImage.src = dataURL;
      maskImage.onload = async () => {
        ctx.drawImage(maskImage, 0, 0, stageDimensions.width, stageDimensions.height);
        const maskB64 = maskCanvas.toDataURL();

        // Call the callback with the generated mask
        onMaskGenerated(maskB64);

         // Log the mask string
        console.log("Generated Mask String (Base64):", maskB64);


        console.log("Mask Dimensions:", {
          width: maskCanvas.width,
          height: maskCanvas.height,
        });

        try {
          const response = await generateImage(
            resizedImageFile, // Send the resized image
            maskB64,
            "woman", // Default prompt
            { path: "ellie", scale: 1.0 } // Proper Lora object with path and scale
// Default model username
          );

          if (response) {
            console.log("Generated Image Response:", response);
            toast({
              title: "Success",
              description: "Image generated successfully!",
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          }
        } catch (err) {
          toast({
            title: "Error",
            description: "Failed to generate image.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      };
    }
  };

  const handleReset = () => {
    setLines([]);
  };

  return (
    <Flex direction="column" align="center" w="100%">
      {!imagePreview ? (
        <div
          {...getRootProps()}
          style={{
            border: "1px solid #68696E",
            borderRadius: "12px",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <Text>Drop your image here!</Text>
          ) : (
            <Text>Click or Drag an image to upload</Text>
          )}
        </div>
      ) : (
        <Flex direction="column" align="center" w="100%" position="relative">
          <div style={{ position: "relative", width: stageDimensions.width, height: stageDimensions.height }}>
            <ChakraImage
              src={imagePreview}
              alt="Uploaded"
              onLoad={handleImageLoad}
              style={{
                maxWidth: "100%",
                borderRadius: "12px",
                position: "absolute",
                width: stageDimensions.width,
                height: stageDimensions.height,
              }}
            />
            <Stage
              width={stageDimensions.width}
              height={stageDimensions.height}
              onMouseDown={handleCursorDown}
              onMouseMove={handleCursorMove}
              onMouseUp={handleCursorUp}
              ref={stageRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Layer>
                {lines.map((line, i) => (
                  <Line
                    key={i}
                    points={line.points}
                    stroke="white"
                    strokeWidth={brushSize}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation="source-over"
                  />
                ))}
              </Layer>
            </Stage>
          </div>
          <Slider
            mt={4}
            min={1}
            max={50}
            value={brushSize}
            onChange={(value) => setBrushSize(value)}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Flex mt={4} gap={4}>
            <Button onClick={handleReset} isDisabled={loading}>
              Reset
            </Button>
            <Button onClick={handleConfirm} isLoading={loading}>
              Confirm
            </Button>
          </Flex>
          {error && (
            <Text color="red.500" mt={4}>
              {error}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default ImageMaskingTool;
